import { Control, FieldValues, Path, useController, useFormContext } from 'react-hook-form';
import { FormItem, FormField } from '@/components/ui/form';
import { Input, InputProps } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import React, { useEffect, useRef, useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Upload, X, Info } from 'lucide-react';
import { OneLineText } from './OneLineText';
import { toast } from './ui/use-toast';
import { EMOJI_REGEX } from '@/utils/constant';

interface FormFieldComponentProps<T extends FieldValues> {
  control: Control<T>;
  defaultFileName?: string;
  name: Path<T>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  rules?: object;
  props?: InputProps;
  className?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  type?: string;
  disabled?: boolean;
  disabledStyle?: string;
  autofocus?: boolean;
  itemClassName?: string;
  pattern?: string;
  maxLength?: number;
  capitalize?: boolean;
  capitalizeFirstLetter?: boolean;
  defaultValue?: string;
  showErrors?: boolean;
  replaceRegex?: RegExp;
  allowNumber?: boolean;
  allowSpecialCharacters?: boolean;
  acceptedFileTypes?: string[];
  allowDecimals?: boolean;
  maxSizeMb?: number;
  allowNegativeNumbers?: boolean;
  maxDecimalPlaces?: number;
  allowMoreThanTwoSameSpecialCharacters?: boolean;
  tooltip?: string;
  step?: string;
}

const FormFieldComponent = <T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  required,
  props,
  defaultFileName,
  className,
  type = 'text',
  disabled,
  showErrors = true,
  onChange,
  onBlur,
  disabledStyle,
  autofocus,
  itemClassName,
  pattern,
  maxLength,
  capitalize,
  capitalizeFirstLetter,
  defaultValue,
  replaceRegex,
  allowNumber = true,
  allowSpecialCharacters = true,
  acceptedFileTypes,
  maxSizeMb = 20,
  allowNegativeNumbers = false,
  maxDecimalPlaces = 2,
  allowDecimals = true,
  allowMoreThanTwoSameSpecialCharacters = true,
  tooltip,
}: FormFieldComponentProps<T>) => {
  const { field } = useController({ name, control });
  const { clearErrors, setError } = useFormContext();

  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState(defaultFileName ?? 'Click to upload a file');
  useEffect(() => {
    if (defaultValue && field.value === undefined) {
      field.onChange(defaultValue);
    }
  }, [defaultValue]);
  useEffect(() => {
    if (autofocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autofocus]);
  return (
    <FormItem className={`flex flex-col gap-1 ${itemClassName ? itemClassName : ''}`}>
      <Label htmlFor={name} className="flex items-center gap-1 text-sm font-medium">
        {label}
        {required && <span className="text-error align-top">*</span>}
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </Label>
      <FormField
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <>
            {(type == 'text' || type == 'password' || !type) && (
              <>
                <Input
                  {...field}
                  type={type}
                  id={name}
                  data-testid={name}
                  onChange={(e) => {
                    const cursorPosition = e.target.selectionStart;
                    e.target.value = e.target.value.replace(/^\s+/, '');
                    e.target.value = e.target.value.replace(EMOJI_REGEX, '');
                    if (!allowNumber) {
                      e.target.value = e.target.value.replace(/[0-9]/g, '');
                    }
                    if (!allowSpecialCharacters) {
                      e.target.value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '');
                    } else if (!allowMoreThanTwoSameSpecialCharacters && allowSpecialCharacters) {
                      e.target.value = e.target.value.replace(/([^a-zA-Z0-9\s])\1+/g, '$1');
                    }
                    if (replaceRegex) {
                      e.target.value = e.target.value.replace(replaceRegex, '');
                    }
                    if (capitalize) {
                      e.target.value = e.target.value.toUpperCase();
                    }
                    if (capitalizeFirstLetter) {
                      e.target.value = e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1);
                    }
                    setTimeout(() => {
                      if (inputRef.current) {
                        inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
                      }
                    }, 0);
                    if (onChange) {
                      onChange(e);
                    }
                    if (e.target.value.length === 0 || !e.target.value) field.onChange(null);
                    else field.onChange(e);
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    if (onBlur) {
                      onBlur(e);
                    }
                  }}
                  defaultValue={defaultValue}
                  value={field.value || ''}
                  placeholder={placeholder}
                  className={`bg-white ${className} ${disabled ? disabledStyle : ''}`}
                  disabled={disabled}
                  {...props}
                  autoFocus={autofocus}
                  ref={inputRef}
                  maxLength={maxLength}
                  onInput={(e) => {
                    (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.slice(0, maxLength);
                  }}
                />
                {fieldState.error && showErrors && <p className="text-error text-xs">{fieldState.error.message}</p>}
              </>
            )}
            {type === 'number' && (
              <>
                <Input
                  {...field}
                  type={type}
                  id={name}
                  data-testid={name}
                  onKeyDown={(e) => {
                    if (!allowNegativeNumbers) {
                      const invalidChars = ['e', 'E', '+', '-'];
                      if (invalidChars.includes(e.key)) {
                        e.preventDefault();
                      }
                    }
                    if (!allowDecimals) {
                      if (e.key === '.' || e.key === ',') {
                        e.preventDefault();
                      }
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (maxLength && value.length > maxLength) return;
                    if (value === '' || value.length === 0) {
                      field.onChange(null);
                      if (onChange) onChange(e);
                      return;
                    }
                    const regex = new RegExp(`^-?\\d*(\\.\\d{0,${maxDecimalPlaces}})?$`);
                    if (regex.test(value)) {
                      field.onChange(e);
                      if (onChange) onChange(e);
                    } else {
                      e.preventDefault?.();
                    }
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    if (onBlur) {
                      onBlur(e);
                    }
                  }}
                  value={field.value || ''}
                  placeholder={placeholder}
                  className={`bg-white number-field ${className} ${disabled ? disabledStyle : ''}`}
                  disabled={disabled}
                  {...props}
                  autoFocus={autofocus}
                  pattern={pattern}
                  {...(!allowNegativeNumbers && {
                    min: 0,
                  })}
                />
                {fieldState.error && showErrors && <p className="text-error text-xs">{fieldState.error.message}</p>}
              </>
            )}
            {(type === 'file' || type === 'multipleFile') && (
              <>
                <div
                  className={`bg-white cursor-pointer flex flex-row border-[1px] border-dashed rounded-md p-2 pr-[20px] gap-2 items-center justify-between h-[48px] w-full ${disabled ? 'text-placeholder border-bordercolor' : 'text-primary border-[#92A1B3]'
                    }`}
                >
                  <Label
                    htmlFor={name}
                    className={`flex cursor-pointer flex-row gap-2 min-w-[200px] ${type === 'file' && fileName !== 'Click to upload a file' ? 'w-[calc(100%-20px)]' : 'w-full'
                      } items-center`}
                  >
                    <Input
                      type="file"
                      id={name}
                      data-testid={name}
                      accept={acceptedFileTypes?.map((f) => `.${f}`).join(',')}
                      disabled={disabled}
                      multiple={type === 'multipleFile'}
                      onChange={(e) => {
                        const files = Array.from(e.target.files ?? []);
                        if (acceptedFileTypes) {
                          const invalidFiles = files.filter(
                            (file) => !acceptedFileTypes.includes(file.name.split('.').pop()?.toLowerCase() ?? '')
                          );
                          if (invalidFiles.length > 0) {
                            e.target.value = '';
                            setError(name, {
                              type: 'manual',
                              message: `File should be either ${acceptedFileTypes
                                .map((f) => f.toUpperCase())
                                .join(', ')
                                .replace(/, ([^,]*)$/, ' or $1')}.`,
                            });
                            field.onChange(null);
                            if (type === 'file') setFileName('Click to upload a file');
                            return;
                          }
                        }

                        if (files.some((file) => file.size > maxSizeMb * 1024 * 1024)) {
                          toast({
                            description: `File size exceeds ${maxSizeMb} MB. Please upload a smaller file.`,
                            duration: 5000,
                            variant: 'destructive',
                          });
                          return;
                        }

                        clearErrors(name);

                        if (type === 'file') {
                          const file = files[0] ?? null;
                          field.onChange(file);
                          setFileName(file ? file.name : 'Click to upload a file');
                        }

                        if (onChange) onChange(e);
                        e.target.value = '';
                      }}
                      onBlur={(e) => {
                        field.onBlur();
                        if (onBlur) onBlur(e);
                      }}
                      className="hidden"
                    />

                    <Upload className={`w-5 h-5 ${disabled ? 'text-placeholder' : 'text-primary'}`} strokeWidth={2} />

                    <OneLineText
                      displayTooltip={false}
                      className={` text-sm max-w-[calc(100%-50px)] ${disabled ? 'text-placeholder' : 'text-primary'}`}
                      text={fileName}
                    />
                  </Label>

                  {type === 'file' && (field.value || fileName !== 'Click to upload a file') && (
                    <button
                      aria-label="Remove file"
                      className="w-4 h-4 cursor-pointer"
                      onClick={() => {
                        field.onChange(null);
                        setFileName('Click to upload a file');
                        if (onChange) {
                          const syntheticEvent = {
                            target: {
                              files: null,
                              value: '',
                            } as HTMLInputElement,
                          } as React.ChangeEvent<HTMLInputElement>;
                          onChange(syntheticEvent);
                        }
                      }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {fieldState.error && showErrors && <p className="text-error text-xs">{fieldState.error.message}</p>}
              </>
            )}
          </>
        )}
      />
    </FormItem>
  );
};

export default FormFieldComponent;
